import { LitElement, html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import { effect } from "@preact/signals-core";
import {
  productMedia,
  activeMediaId,
  setActiveMedia,
} from "../store/product-store";

declare global {
  interface Window {
    pauseAllMedia?: () => void;
  }
}

@customElement("product-gallery-lit")
export class ProductGallery extends LitElement {
  @state() private _media: any[] = [];
  @state() private _activeMediaId: number | null = null;
  @state() private _canScrollUp = false;
  @state() private _canScrollDown = false;
  @state() private _canScrollLeft = false;
  @state() private _canScrollRight = false;

  private _sizeChartDelegate: ((e: Event) => void) | null = null;
  private _desktopScrollHandler: (() => void) | null = null;
  private _mobileScrollHandler: (() => void) | null = null;

  constructor() {
    super();
    effect(() => {
      this._media = productMedia.value;
      this._activeMediaId = activeMediaId.value;
    });
  }

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    this._sizeChartDelegate = (e: Event) => {
      const target = (e.target as HTMLElement).closest('a[href="#sizechart"]');
      if (!target) return;
      e.preventDefault();
      const lastMedia = this._media[this._media.length - 1];
      if (lastMedia) {
        setActiveMedia(lastMedia.id);
        this.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };
    document.addEventListener("click", this._sizeChartDelegate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._sizeChartDelegate) {
      document.removeEventListener("click", this._sizeChartDelegate);
      this._sizeChartDelegate = null;
    }
    this._removeScrollListeners();
  }

  firstUpdated() {
    this._attachScrollListeners();
  }

  updated() {
    // Desktop and mobile strips render thumbnails with the same data-thumb-id,
    // so scope the lookup to each container and scroll them independently.
    this._scrollActiveThumbIntoView(".gallery-thumbs-desktop", "vertical");
    this._scrollActiveThumbIntoView(".gallery-thumbs-mobile", "horizontal");
    // Re-attach listeners if DOM changed (e.g. media list populated)
    this._attachScrollListeners();
    requestAnimationFrame(() => this._updateScrollFlags());
  }

  private _scrollActiveThumbIntoView(
    containerSelector: string,
    axis: "vertical" | "horizontal"
  ) {
    const container = this.querySelector<HTMLElement>(containerSelector);
    if (!container) return;
    const activeLi = container.querySelector<HTMLElement>(
      `[data-thumb-id="${this._activeMediaId}"]`
    );
    if (!activeLi) return;

    const c = container.getBoundingClientRect();
    const t = activeLi.getBoundingClientRect();

    // Only scroll when the active thumb is out of view; align it to the
    // leading (left/top) edge of the visible strip.
    if (axis === "horizontal") {
      if (t.left < c.left || t.right > c.right) {
        container.scrollBy({ left: t.left - c.left, behavior: "smooth" });
      }
    } else {
      if (t.top < c.top || t.bottom > c.bottom) {
        container.scrollBy({ top: t.top - c.top, behavior: "smooth" });
      }
    }
  }

  // --- Scroll tracking ---

  private _attachScrollListeners() {
    const desktopList = this.querySelector<HTMLElement>(
      ".gallery-thumbs-desktop"
    );
    const mobileList = this.querySelector<HTMLElement>(
      ".gallery-thumbs-mobile"
    );

    if (desktopList && !this._desktopScrollHandler) {
      this._desktopScrollHandler = () => this._updateScrollFlags();
      desktopList.addEventListener("scroll", this._desktopScrollHandler, {
        passive: true,
      });
    }
    if (mobileList && !this._mobileScrollHandler) {
      this._mobileScrollHandler = () => this._updateScrollFlags();
      mobileList.addEventListener("scroll", this._mobileScrollHandler, {
        passive: true,
      });
    }
  }

  private _removeScrollListeners() {
    const desktopList = this.querySelector<HTMLElement>(
      ".gallery-thumbs-desktop"
    );
    const mobileList = this.querySelector<HTMLElement>(
      ".gallery-thumbs-mobile"
    );
    if (desktopList && this._desktopScrollHandler) {
      desktopList.removeEventListener("scroll", this._desktopScrollHandler);
      this._desktopScrollHandler = null;
    }
    if (mobileList && this._mobileScrollHandler) {
      mobileList.removeEventListener("scroll", this._mobileScrollHandler);
      this._mobileScrollHandler = null;
    }
  }

  private _updateScrollFlags() {
    const desktopList = this.querySelector<HTMLElement>(
      ".gallery-thumbs-desktop"
    );
    if (desktopList) {
      this._canScrollUp = desktopList.scrollTop > 1;
      this._canScrollDown =
        desktopList.scrollTop + desktopList.clientHeight <
        desktopList.scrollHeight - 1;
    }
    const mobileList = this.querySelector<HTMLElement>(
      ".gallery-thumbs-mobile"
    );
    if (mobileList) {
      this._canScrollLeft = mobileList.scrollLeft > 1;
      this._canScrollRight =
        mobileList.scrollLeft + mobileList.clientWidth <
        mobileList.scrollWidth - 1;
    }
  }

  private _scrollThumbnails(
    direction: "up" | "down" | "left" | "right"
  ) {
    if (direction === "up" || direction === "down") {
      const list = this.querySelector<HTMLElement>(".gallery-thumbs-desktop");
      if (list)
        list.scrollBy({
          top: direction === "down" ? 128 : -128,
          behavior: "smooth",
        });
    } else {
      const list = this.querySelector<HTMLElement>(".gallery-thumbs-mobile");
      if (list)
        list.scrollBy({
          left: direction === "right" ? 78 : -78,
          behavior: "smooth",
        });
    }
  }

  // --- Navigation ---

  private _handleThumbnailClick(mediaId: number) {
    window.pauseAllMedia?.();
    setActiveMedia(mediaId);
  }

  private _handleNavClick(direction: "prev" | "next") {
    if (!this._activeMediaId) return;
    const currentIndex = this._media.findIndex(
      (m) => m.id === this._activeMediaId
    );
    if (currentIndex === -1) return;
    const nextIndex =
      direction === "next" ? currentIndex + 1 : currentIndex - 1;
    if (nextIndex < 0 || nextIndex >= this._media.length) return;
    window.pauseAllMedia?.();
    setActiveMedia(this._media[nextIndex].id);
  }

  // --- Media helpers ---

  private _getImageSrc(media: any): string | null {
    const src =
      media.preview_image?.src ??
      (media.media_type === "image" ? media.src : undefined);
    if (!src) return null;
    return src.startsWith("//") ? `https:${src}` : src;
  }

  // --- Arrow classes ---

  private _arrowClasses(side: "prev" | "next"): string {
    return [
      "absolute top-1/2 -translate-y-1/2 z-10",
      side === "prev" ? "left-3" : "right-3",
      "bg-[#eafcff] rounded-full w-[35px] h-[35px]",
      "flex items-center justify-center",
      "shadow-[0px_10px_38px_-10px_rgba(0,0,0,0.75)]",
      // Slide-in from edges + fade. Mobile: always visible and in place.
      side === "prev"
        ? "opacity-0 -translate-x-[100px] group-hover:opacity-100 group-hover:translate-x-0 max-lg:opacity-100 max-lg:translate-x-0"
        : "opacity-0 translate-x-[100px] group-hover:opacity-100 group-hover:translate-x-0 max-lg:opacity-100 max-lg:translate-x-0",
      "transition-all duration-300",
      "disabled:hidden",
      "cursor-pointer",
    ].join(" ");
  }

  // --- Caret SVG ---

  private _caretSvg(direction: "left" | "right" | "up" | "down") {
    const points: Record<string, string> = {
      left: "15 18 9 12 15 6",
      right: "9 18 15 12 9 6",
      up: "18 15 12 9 6 15",
      down: "18 9 12 15 6 9",
    };
    return html`<svg
      aria-hidden="true"
      focusable="false"
      role="presentation"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      class="w-[0.8rem] h-[0.8rem] fill-none stroke-[#22355b]"
      stroke-width="2"
    >
      <polyline points="${points[direction]}"></polyline>
    </svg>`;
  }

  // --- Media type icon overlays for thumbnails ---

  private _mediaTypeIcon(mediaType: string) {
    if (mediaType === "video" || mediaType === "external_video") {
      return html`<span
        class="absolute bottom-1 right-1 w-4 h-4 bg-black/50 rounded-full flex items-center justify-center"
      >
        <svg
          viewBox="0 0 16 16"
          class="w-2.5 h-2.5 fill-white"
          xmlns="http://www.w3.org/2000/svg"
        >
          <polygon points="4,2 14,8 4,14"></polygon>
        </svg>
      </span>`;
    }
    if (mediaType === "model") {
      return html`<span
        class="absolute bottom-1 right-1 w-4 h-4 bg-black/50 rounded-full flex items-center justify-center"
      >
        <svg
          viewBox="0 0 16 16"
          class="w-2.5 h-2.5 fill-white"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M8 1L14 4.5V11.5L8 15L2 11.5V4.5L8 1Z"></path>
        </svg>
      </span>`;
    }
    return nothing;
  }

  // --- Main media rendering (image / video / 3D model) ---

  private _renderMainMedia(media: any) {
    const src = this._getImageSrc(media);
    if (!src) return nothing;

    if (media.media_type === "image") {
      return html`<img
        src="${src}"
        alt="${media.alt ?? ""}"
        loading="eager"
        width="${media.width}"
        height="${media.height}"
        class="absolute inset-0 w-full h-full object-contain"
      />`;
    }

    if (
      media.media_type === "video" ||
      media.media_type === "external_video"
    ) {
      return html`
        <deferred-media
          class="deferred-media absolute inset-0 w-full h-full"
          data-media-id="${media.id}"
          style="padding-top: 0;"
        >
          <button
            id="Deferred-Poster-${media.id}"
            class="deferred-media__poster w-full h-full"
            type="button"
          >
            <span class="deferred-media__poster-button motion-reduce">
              <span class="sr-only">Play video</span>
              <svg
                class="w-12 h-12"
                viewBox="0 0 64 64"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="32" cy="32" r="32" fill="rgba(0,0,0,0.4)" />
                <polygon points="26,20 46,32 26,44" fill="white" />
              </svg>
            </span>
            <img
              src="${src}"
              alt="${media.alt ?? ""}"
              loading="eager"
              class="absolute inset-0 w-full h-full object-contain"
            />
          </button>
          <template>
            ${media.media_type === "video"
              ? html`<video
                  playsinline
                  controls
                  autoplay
                  style="width:100%;height:100%;object-fit:contain;"
                >
                  ${(media.sources ?? []).map(
                    (s: any) =>
                      html`<source src="${s.url}" type="${s.mime_type}" />`
                  )}
                </video>`
              : html`<iframe
                  src="${this._getExternalVideoUrl(media)}"
                  allow="autoplay; encrypted-media"
                  allowfullscreen
                  style="width:100%;height:100%;border:0;"
                ></iframe>`}
          </template>
        </deferred-media>
      `;
    }

    if (media.media_type === "model") {
      return html`
        <product-model
          class="deferred-media absolute inset-0 w-full h-full"
          data-media-id="${media.id}"
          style="padding-top: 0;"
        >
          <button
            id="Deferred-Poster-${media.id}"
            class="deferred-media__poster w-full h-full"
            type="button"
          >
            <span class="deferred-media__poster-button motion-reduce">
              <span class="sr-only">View 3D model</span>
              <svg
                class="w-12 h-12"
                viewBox="0 0 64 64"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="32" cy="32" r="32" fill="rgba(0,0,0,0.4)" />
                <path
                  d="M32 18L46 26V42L32 50L18 42V26L32 18Z"
                  stroke="white"
                  stroke-width="2"
                  fill="none"
                />
              </svg>
            </span>
            <img
              src="${src}"
              alt="${media.alt ?? ""}"
              loading="eager"
              class="absolute inset-0 w-full h-full object-contain"
            />
          </button>
          <template>
            ${media.sources?.[0]?.url
              ? html`<model-viewer
                  src="${media.sources[0].url}"
                  camera-controls
                  interaction-prompt="none"
                  style="width:100%;height:100%;"
                ></model-viewer>`
              : nothing}
          </template>
        </product-model>
      `;
    }

    // Fallback: render as image
    return html`<img
      src="${src}"
      alt="${media.alt ?? ""}"
      loading="eager"
      class="absolute inset-0 w-full h-full object-contain"
    />`;
  }

  private _getExternalVideoUrl(media: any): string {
    if (media.host === "youtube") {
      return `https://www.youtube.com/embed/${media.external_id}?autoplay=1`;
    }
    if (media.host === "vimeo") {
      return `https://player.vimeo.com/video/${media.external_id}?autoplay=1`;
    }
    return media.sources?.[0]?.url ?? "";
  }

  // --- Thumbnail rendering ---

  private _renderThumbnail(
    media: any,
    size: "desktop" | "mobile"
  ) {
    const thumbSrc = this._getImageSrc(media);
    const isActive = media.id === this._activeMediaId;
    const sizeClass =
      size === "desktop"
        ? "w-[120px] h-[120px]"
        : "w-[70px] h-[70px]";

    return html`
      <li
        data-thumb-id="${media.id}"
        class="relative flex-none ${sizeClass} cursor-pointer rounded overflow-hidden border ${isActive
          ? "border-gray-500"
          : "border-gray-200"}"
        @click=${() => this._handleThumbnailClick(media.id)}
      >
        ${thumbSrc
          ? html`<img
              src="${thumbSrc}"
              alt="${media.alt ?? ""}"
              loading="lazy"
              class="w-full h-full object-contain"
            />`
          : nothing}
        ${this._mediaTypeIcon(media.media_type)}
      </li>
    `;
  }

  // --- Thumbnail scroll button ---

  private _thumbScrollBtn(
    direction: "up" | "down" | "left" | "right",
    disabled: boolean,
    size: "desktop" | "mobile"
  ) {
    const sizeClass =
      size === "desktop"
        ? "w-[35px] h-[35px]"
        : "w-[20px] h-[20px]";

    return html`<button
      type="button"
      aria-label="Scroll thumbnails ${direction}"
      ?disabled=${disabled}
      @click=${() => this._scrollThumbnails(direction)}
      class="${sizeClass} bg-[#eafcff] rounded-full flex items-center justify-center cursor-pointer disabled:opacity-30 disabled:cursor-default flex-none"
    >
      ${this._caretSvg(direction)}
    </button>`;
  }

  // --- Render ---

  render() {
    if (this._media.length === 0) {
      return html`<div class="p-4 text-sm text-gray-400">
        Loading gallery...
      </div>`;
    }

    const activeMedia = this._media.find((m) => m.id === this._activeMediaId);
    const currentIndex = this._media.findIndex(
      (m) => m.id === this._activeMediaId
    );
    const isFirst = currentIndex === 0;
    const isLast = currentIndex === this._media.length - 1;
    const hasMultiple = this._media.length > 1;

    return html`
      <div class="${hasMultiple ? 'flex flex-col lg:grid lg:grid-cols-[120px_1fr] lg:gap-4' : ''}"
        <!-- Desktop: vertical thumbnail sidebar (left) -->
        ${hasMultiple
          ? html`
              <div class="hidden lg:block relative">
                <div class="absolute inset-0 flex flex-col items-center">
                  <div class="flex justify-center mb-2 flex-none">
                    ${this._thumbScrollBtn("up", !this._canScrollUp, "desktop")}
                  </div>
                  <ul
                    class="gallery-thumbs-desktop flex flex-col gap-2 overflow-y-auto list-none p-0 m-0 flex-1 min-h-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                  >
                    ${this._media.map((m) =>
                      this._renderThumbnail(m, "desktop")
                    )}
                  </ul>
                  <div class="flex justify-center mt-2 flex-none">
                    ${this._thumbScrollBtn(
                      "down",
                      !this._canScrollDown,
                      "desktop"
                    )}
                  </div>
                </div>
              </div>
            `
          : nothing}

        <!-- Main image area -->
        <div class="flex-1 min-w-0">
          <div
            class="group relative aspect-square overflow-hidden rounded-lg border border-gray-200"
          >
            ${activeMedia ? this._renderMainMedia(activeMedia) : nothing}
            ${hasMultiple
              ? html`
                  <button
                    type="button"
                    aria-label="Previous slide"
                    @click=${() => this._handleNavClick("prev")}
                    ?disabled=${isFirst}
                    class="${this._arrowClasses("prev")}"
                  >
                    ${this._caretSvg("left")}
                  </button>
                  <button
                    type="button"
                    aria-label="Next slide"
                    @click=${() => this._handleNavClick("next")}
                    ?disabled=${isLast}
                    class="${this._arrowClasses("next")}"
                  >
                    ${this._caretSvg("right")}
                  </button>
                `
              : nothing}
          </div>

          <!-- Mobile: horizontal thumbnail strip (below) -->
          ${hasMultiple
            ? html`
                <div
                  class="flex lg:hidden items-center gap-1 mt-2"
                >
                  ${this._thumbScrollBtn(
                    "left",
                    !this._canScrollLeft,
                    "mobile"
                  )}
                  <ul
                    class="gallery-thumbs-mobile flex overflow-x-auto gap-2 list-none p-0 m-0 flex-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                  >
                    ${this._media.map((m) =>
                      this._renderThumbnail(m, "mobile")
                    )}
                  </ul>
                  ${this._thumbScrollBtn(
                    "right",
                    !this._canScrollRight,
                    "mobile"
                  )}
                </div>
              `
            : nothing}
        </div>
      </div>
    `;
  }
}
